/**
 * User: Gedas
 * Date: 4/9/15
 * Time: 3:00 PM
 * Copyright 1stdibs.com, Inc. 2015. All Rights Reserved.
 */

'use strict';

var get = require('lodash.get');
var includes = require('lodash.includes');

// ---------------------------------------------------
// Load Super Class

var SoaModel = require('bunsen/models/SoaModel');

// ---------------------------------------------------
// Module Definition

/**
 * @class User
 * @extends SoaModel
 */
var User = module.exports = SoaModel.extend();
var root = typeof window === 'undefined' ? global : window;

// ---------------------------------------------------
// Local Variables

var users = {};
var promises = {};
var triedGettingUserFromSV;

// ---------------------------------------------------
// Dependencies

var $                   = require('dibs-jquery-shim');
var _                   = require('underscore');
var Backbone            = require('dibs-backbone');
var endpoints           = require('dibs-endpoints');
var Role                = require('bunsen/models/identity/Role');
var Roles               = require('bunsen/collections/identity/Roles');
var UserSocialNetworks  = require('bunsen/collections/identity/UserSocialNetworks');
var Permissions         = require('bunsen/models/identity/Permissions');
var Permission          = require('bunsen/models/identity/Permission');
var UserProfile         = require('bunsen/models/identity/UserProfile');
var UserProProfile      = require('bunsen/models/identity/UserProProfile');
var Newsletters         = require('bunsen/models/Newsletters');
var AddressBook         = require('bunsen/models/identity/AddressBook');
var Note                = require('bunsen/models/Note');
var moment              = require('moment');
var SV                  = require('server-vars');
var Portfolio           = require('bunsen/models/portfolio/PortfolioModel');
var PortfolioCollection = require('bunsen/collections/portfolio/PortfolioCollection');
var Notes               = require('bunsen/collections/Notes');
var log                 = require('bunsen/helpers/log');
var defaultCurrency     = require('bunsen/models/FinancialAmount').CURRENCY_USD;

require('bunsen/libraries/plugins/jquery.cookie'); // jQuery.fn.cookie

// ---------------------------------------------------

if (root.bunsen) {
    root.bunsen.User = root.User = User;
}

// ---------------------------------------------------
// Define Module Constants

_.extend(User, {
    COOKIE_USER             : 'userToken',
    COOKIE_USER_LEGACY      : 'userreg',
    COOKIE_USER_MASQUERADE  : 'masqueradeBuyer',
    COOKIE_USER_TYPE        : 'userType',
    COOKIE_GUEST_ID         : 'guestId',
    COOKIE_EVER_LOGGED_IN   : 'eli',
    COOKIE_PASSWORD_RESET   : 'passwordToken',
    FIELD_FULL_PROFILE      : 'fullProfile',
    FIELD_MIN_PROFILE       : 'minProfile',
    FIELD_ADDRESS           : 'address',
    FIELD_CONVERSATION      : 'CONVERSATION_SUMMARY',
    FIELD_PERMISSIONS       : 'permissions',
    FIELD_WALLET            : 'wallet',
    FIELD_CART              : 'CART',
    FIELD_ROLES             : 'roles',
    FIELD_SOCIAL_NETWORKS   : 'SOCIAL_NETWORK',
    MAX_AGE_ECOM            : 'ECOM_OFFER',
    MAX_AGE_ECOM_WITH_OFFSET: 'ECOM_OFFER.minus(480)',
    MAX_AGE_SAVE_PASSWORD   : 'SAVE_PASSWORD.minus(120)',
    TYPE_SELLER             : 'seller',
    TYPE_INTERNAL           : 'internal',
    BUYER_TRADE             : 'B_TRADE_BUYER',
    BUYER_VIP               : 'B_VIP_BUYER',
    USER_CURRENT            : 'current',
    USER_REAL               : 'real',
    TRADE_STATUSES          : {
        N_A: 'N_A',
        SUSPECTED: 'SUSPECTED',
        APPLIED_IN_REVIEW: 'APPLIED_IN_REVIEW',
        APPLIED_NOT_ENOUGH_INFORMATION: 'APPLIED_NOT_ENOUGH_INFORMATION',
        APPLIED_REJECTED: 'APPLIED_REJECTED',
        VERIFIED_PRE_APPROVED: 'VERIFIED_PRE_APPROVED',
        VERIFIED_VETTED: 'VERIFIED_VETTED'
    }
});

// ---------------------------------------------------
// Define Module Instance Properties

_.extend(User.prototype, {

    idAttribute: 'id',

    url: function () {
        return endpoints.userById(this.get(this.idAttribute));
    },

    relations: function () {
        return [
            {
                key: 'profile',
                relatedModel : UserProfile,
                type: Backbone.One
            },
            {
                key: 'proProfile',
                relatedModel: UserProProfile,
                type: Backbone.One
            },
            {
                key: 'addressBook',
                relatedModel: AddressBook,
                type: Backbone.One
            },
            {
                key: 'newsletters',
                relatedModel: Newsletters,
                type: Backbone.One
            },
            {
                key: 'portfolio',
                relatedModel: Portfolio,
                type: Backbone.One
            },
            {
                key: 'roles',
                collectionType: Roles,
                type: Backbone.Many,
                omitFromSave: true
            },
            {
                key: 'permissions',
                relatedModel: Permissions,
                type: Backbone.One,
                omitFromSave: true
            },
            {
                key: 'wallet',
                type: Backbone.One,
                omitFromSave: true,
                relatedModel: SoaModel
            },
            {
                key: 'conversationSummary',
                relatedModel: SoaModel,
                type: Backbone.One
            },
            {
                key: 'socialNetworks',
                collectionType: UserSocialNetworks,
                type: Backbone.Many
            }
        ];
    },

    /**
     * @param {String} url
     * @param {String} email
     * @param {String} password
     * @param {String} [role]
     * @returns {Promise}
     * @private
     */
    _authenticate: function (url, email, password, roleName) {
        var data = this.toJSON();

        var userData = {
            email: email,
            password: password
        };

        /* roleName should only be defined when registering with a pre-set role */
        if (roleName) {
            userData.role = { name: roleName };
        }

        return this.clear().save(null, {
            url: url,
            attrs: userData
        }).always(function () {
            this.set(_.extend(data, this.toJSON()));
        }.bind(this));
    },

    isInitialized: function () {
        var id = this.get('id');
        return id && id !== 'me';
    },

    /**
     * @returns {Object}
     */
    getValuesForDetailDisplayString: function () {
        var profile = this.get('profile');
        return {
            profile: profile,
            displayName: profile.getDisplayName(),
            id: this.get('id'),
            email: profile.get('email'),
            registeredLabel: 'Registered',
            registeredDateString: moment(this.get('createdDate')).format('l')
        };
    },

    /**
     * @param {*} perm - a single permission or array of permissions
     * @returns {*}
     */
    hasPermission: function (perm) {
        var permissions = this.get('permissions');
        if (permissions) {
            return permissions.checkPermissions(perm);
        } else {
            return null;
        }
    },

    /**
     * @param {String} roleName
     * @returns {Boolean|null}
     */
    hasRole: function (roleName) {
        var roles = this.get('roles');
        if (!roles) {
            // We don't know this user's roles
            return null;
        } else {
            // Returns true if user has a role with this name
            return roles.some(function (role) {
                return role.get('name') === roleName;
            });
        }
    },

    /**
     * @param {String} roleName
     * @param {Object} options
     * @returns {*}
     */
    addRole: function (roleName, options) {
        var role = new Role({name: roleName});
        this.get('roles').add(role);
        return role.saveRoleForUser(options);
    },

    /**
     * @param {String} roleName
     * @param {Object} options
     * @returns {*}
     */
    removeRole: function (roleName, options) {
        var role = this.get('roles').findWhere({name: roleName});
        return role.removeRoleFromUser(options);
    },

    hasTradeBuyerPermission: function () {
        return this.hasPermission(Permission.PERMISSION_B_NET_PRICE) || this.hasPermission(Permission.PERMISSION_B_TRADE_CHECKOUT);
    },

    /**
     * @returns {String|null}
     */
    getTradeStatus: function () {
        var roles = this.get('roles');
        var tradeRole;

        if (!roles) {
            return null;
        } else {
            tradeRole = roles.find(function (role) {
                return role.get('name') === User.BUYER_TRADE;
            });

            if (tradeRole && tradeRole.get) {
                return get(tradeRole.get('userData'), 'userStatus.name', User.TRADE_STATUSES.N_A);
            }

            return User.TRADE_STATUSES.N_A;
        }
    },

    /**
     * @returns {Boolean|null}
     */
    isVerifiedTradeBuyer: function () {
        var tradeStatus = this.getTradeStatus();
        var verifiedStatuses = [User.TRADE_STATUSES.VERIFIED_PRE_APPROVED, User.TRADE_STATUSES.VERIFIED_VETTED];

        if (tradeStatus === null) {
            return null;
        } else {
            return includes(verifiedStatuses, tradeStatus);
        }
    },

    /**
     * @returns {Boolean|null}
     */
    isVipBuyer: function () {
        return this.hasRole(User.BUYER_VIP);
    },
    /**
     * @param {String} itemPk
     * @returns {*}
     */
    getPortfoliosWithItem: function (itemPk) {
        var portfolios = new PortfolioCollection();
        return portfolios.fetch({data : {userId : this.get('id'), itemId : itemPk}});
    },

    /**
     * @param {String} itemPk
     * @param {String} section
     * @returns {*}
     */
    addItemToDefaultPortfolio: function (itemPk, section) {
        var portfolio = this.get('portfolio');
        if (!portfolio) {
            portfolio = new Portfolio({id:0});
        }
        return portfolio.addItem(itemPk, section, {queries: {userId: this.get('id')}});
    },

    /**
     * @param {String} itemPk
     * @param {String} section
     * @returns {*}
     */
    removeItemFromDefaultPortfolio: function (itemPk, section) {
        var portfolio = this.get('portfolio');
        if (!portfolio) {
            portfolio = new Portfolio({id:0});
        }
        return portfolio.removeItem(itemPk, section, {queries: {userId: this.get('id')}});
    },

    /**
     * @param {Object} passedOptions
     * @returns {*}
     */
    newAccountNote: function (passedOptions) {
        _.defaults(passedOptions, {
            type: Note.TYPE_ADMIN_BUYER_ACCOUNT,
            anchors: [
                {
                    sourceId: this.id,
                    sourceType: 'USER'
                }
            ]
        });
        return Notes.newNote(passedOptions);
    },

    /**
     * @param {Object} noteOptions
     * @param {Object} syncOptions
     * @returns {*}
     */
    addNewAccountNote: function (noteOptions, syncOptions) {
        var note = this.newAccountNote(noteOptions);
        return note.save(null, syncOptions);
    },

    /**
     * @returns {Boolean}
     */
    isSeller: function () {
        return this.get('type') === User.TYPE_SELLER;
    },

    /**
     * @param {String} email
     * @param {String} password
     * @returns {Promise}
     */
    login: function (email, password) {
        return this._authenticate(endpoints.userLogin(), email, password);
    },

    /**
     * @param {String} email
     * @returns {Promise}
     */
    checkEmail: function (email) {
        return this.fetch({
            url: endpoints.userRegister(email),
            type: 'HEAD'
        });
    },

    /**
     * @param {String} email
     * @param {String} password
     * @returns {Promise}
     */
    register: function (email, password) {
        return this._authenticate(endpoints.userRegister(), email, password);
    },

    /**
     * @param {String} email
     * @param {String} password
     * @param {String} role
     * @returns {Promise}
     */
    registerWithRole: function (email, password, role) {
        return this._authenticate(endpoints.userRegister(), email, password, role);
    },

    /**
     * @param {String} email
     * @returns {Promise}
     */
    forgotPassword: function (email) {
        return this.destroy({
            url: endpoints.userPassword(User.getUserIdMe(), {
                email: email
            })
        });
    },

    /**
     * @param {String} password
     * @returns {Promise}
     */
    resetPassword: function (password) {
        return this.save(null, {
            url: endpoints.userPassword(User.getUserIdMe(), {
                passwordToken: $.cookie(User.COOKIE_PASSWORD_RESET)
            }),
            attrs: {
                password: password
            }
        });
    },

    /**
     * @param {String} currentPassword
     * @param {String} newPassword
     * @returns {Promise}
     */
    updatePassword: function (currentPassword, newPassword) {
        var self = this;

        return User.getCurrentUserPromise().then(function () {
            return self.login(self.get('profile.email'), currentPassword).then(function () {
                self.save(null, {
                    url: endpoints.userPassword(User.getUserIdMe(), {
                        userToken: self.get('token')
                    }),
                    attrs: {
                        password: newPassword
                    }
                }).then(function (response) {
                    if (response.result && response.result.user) {
                        self.set(response.result.user);
                    }
                    return response;
                });
            });
        });
    },

    /**
     * @param {String} guestId
     * @returns {Promise}
     */
    saveGuestId: function (guestId) {
        this.sync('create', this, {
            url: endpoints.userSourceAttribution(this.get('id')),
            attrs: {
                elementList: [{
                    type: 'GUEST_USER',
                    date: moment().format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
                    utmParams: { guestUserId: guestId }
                }]
            }
        });
    },
    getProCompanyName: function () {
        var addressBook = this.get('addressBook');
        var proAddress;
        if (!addressBook) {
            return '';
        }
        proAddress = addressBook.getProAddress();
        if (proAddress) {
            return proAddress.get('company');
        }
    },
    getCityState: function () {
        var addressBook = this.get('addressBook');
        var addresses = [];
        var addressList;
        var address;
        if (addressBook) {
            addressList = addressBook.get('addressList');
            if (addressList) {
                addresses.push(addressBook.getPersonalAddress());
                addresses.push(addressList.first());
            }
        }
        addresses.push(this.get('profile'));
        address = _.chain(addresses).compact().first().value();
        if (!address || address.get('country') === "-1") {
            return '';
        }
        return address.getDisplayLocation();
    }
});

// ---------------------------------------------------
// Define Module Static Properties

_.extend(User, {
    /**
     * @param {Object} attrs
     * @returns {User}
     * @private
     */
    _createInstance: function (attrs) {
        return new User(attrs);
    },

    /**
     * @param {String} key
     * @param {Boolean} force
     * @returns {User}
     * @private
     */
    _getUser: function (key, force) {
        var id;
        if (!users[key] || force) {
            id = this.getUserIdMe(key === User.USER_REAL);
            users[key] = this._createInstance({id: id});
        }

        return users[key];
    },

    /**
     * @param {String} key
     * @private
     */
    _clearUser: function (key) {
        if (users[key]) {
            users[key] = null;
        }
    },

    /**
     * @param {String} key
     * @param {Object} options
     * @returns {Promise}
     * @private
     */
    _fetchUser: function (key, options) {
        var user = this._getUser(key);
        options = options || {};

        return user.fetch(options)
            .then(function () {
                return user;
            });
    },
    /**
     * @param {String} key
     * @param {Boolean} force
     * @param {Object} options
     * @returns {Promise}
     * @private
     */
    _getUserPromise: function (key, force, options) {
        if (!key) {
            return null;
        }

        if (!promises[key] || force) {
            promises[key] = this._fetchUser(key, options);
        }

        return promises[key];
    },

    /**
     * @param {String} key
     * @private
     */
    _clearUserPromise: function (key) {
        if (promises[key]) {
            promises[key] = null;
        }
    },

    /**
     * @param {Boolean} [getRealUser] - skip masquerading
     * @returns {String}
     */
    getUserId: function (getRealUser) {
        // jank work-around for GOODS-2407
        // todo User model needs to be made Universal (isomorphic)
        if (!$.cookie || typeof document === 'undefined') {
            return null;
        }

        var userMasqueradeCookie = $.cookie(User.COOKIE_USER_MASQUERADE); // eslint-disable-line vars-on-top
        var userCookie = $.cookie(User.COOKIE_USER); // eslint-disable-line vars-on-top

        if (userMasqueradeCookie && !getRealUser) {
            return userMasqueradeCookie.slice(2);
        } else {
            return userCookie ? userCookie.substr(0, userCookie.indexOf('_')) : null;
        }
    },

    /**
     * @param {Boolean} getRealUser - skip masquerading
     * @returns {String}
     */
    getUserIdMe: function (getRealUser) {
        return this.getUserId(getRealUser) || 'me';
    },

    /**
     * Return user token
     *
     * @returns {string}
     */
    getUserToken: function () {
        return $.cookie(User.COOKIE_USER);
    },

    /**
     * @returns {Boolean}
     */
    loggedIn: function () {
        return !!this.getUserToken();
    },

    /**
     * @returns {Boolean}
     */
    everLoggedIn: function () {
        return !!$.cookie(User.COOKIE_EVER_LOGGED_IN);
    },

    /**
     * @returns {Boolean}
     */
    isMasquerading: function () {
        return !!$.cookie(User.COOKIE_USER_MASQUERADE);
    },

    /**
     * @param {Boolean} force
     * @param {Object} options
     * @returns {Promise}
     */
    getCurrentUserPromise: function (force, options) {
        return this._getUserPromise(User.USER_CURRENT, force, options);
    },

    /**
     * Null out the current user promise. Should be mostly helpful for testing
     */
    clearCurrentUserPromise: function () {
        this._clearUserPromise(User.USER_CURRENT);
    },

    /**
     * Return the current user singleton
     * @returns {User}
     */
    getCurrentUser: function () {
        return this._getUser(User.USER_CURRENT);
    },

    /**
     * Null out the current user. Should be mostly helpful for testing
     */
    clearCurrentUser: function () {
        this._clearUser(User.USER_CURRENT);
    },

    /**
     * @returns {Promise}
     */
    getRealUserPromise: function () {
        return this._getUserPromise(User.USER_REAL);
    },

    /**
     * Null out the real user promise. Should be mostly helpful for testing
     */
    clearRealUserPromise: function () {
        this._clearUserPromise(User.USER_REAL);
    },

    /**
     * Return the real user singleton.
     * @returns {User}
     */
    getRealUser: function () {
        return this._getUser(User.USER_REAL);
    },

    /**
     * Null out the real user. Should be mostly helpful for testing
     */
    clearRealUser: function () {
        this._clearUser(User.USER_REAL);
    },

    /**
     * Returns the current user singleton.
     * Will instantiate it if an `attrs` hash is passed and there it hasn't been instantiated yet.
     *
     * @param {Object} attrs - An attributes hash to instantiate a new user singleton
     * @returns {User|null}
     */
    getCurrentUserInit: function (attrs) {
        if (!users[User.USER_CURRENT] && attrs) {
            users[User.USER_CURRENT] = this._createInstance(attrs);
        }
        return users[User.USER_CURRENT];
    },

    /**
     * Returns the current user singleton.
     * Will instantiate it from __SERVER_VARS__.userData hash if it hasn't been instantiated yet.
     * @returns {User}
     */
    getCurrentUserSV: function () {
        return this.getCurrentUserInit(SV.userData);
    },

    /**
     * Returns a promise that will resolve to the current user.
     * @returns {Promise}
     */
    gotCurrentUser: function () {
        var currentUserPromise = promises[User.USER_CURRENT];
        var userFromSV;

        if (currentUserPromise) {
            return currentUserPromise;
        }
        if (!triedGettingUserFromSV) {
            triedGettingUserFromSV = true;
            userFromSV = this.getCurrentUserSV();
            if (userFromSV) {
                promises[User.USER_CURRENT] = $.Deferred().resolve(userFromSV);
            }
        }
        if (!currentUserPromise) {
            currentUserPromise = this.getCurrentUserPromise();
        }
        return currentUserPromise;
    },

    /**
     * @param {Boolean} force - Force fetching current user from the server
     * @returns {Promise|*}
     */
    fetchCurrentUser : function (force, options) {
        var user = this._getUser(User.USER_CURRENT, force);

        return user.fetch(options).then(function (data) {
            if (!user.get('profile')) {
                log.remote.error(
                    JSON.stringify({
                        "User object from services does not have a profile property; something is awry" : data
                    })
                );
            }
            return user;
        });
    },

    /**
     * Tests if the user token is "fresh" enough to checkout
     * @returns {Promise}
     */
    canCurrentUserCheckout: function (options) {
        options = _.extend({}, options);
        options.data = _.extend(options.data || {}, {
            maxAge: User.MAX_AGE_ECOM_WITH_OFFSET
        });

        return User.fetchCurrentUser(false, options);
    },

    /**
     * @returns {String}
     */
    getCurrentUserCurrency: function () {
        var user = User.getCurrentUser();
        return user && user.get('profile.currency') || defaultCurrency;
    }
});



//////////////////
// WEBPACK FOOTER
// ./~/bunsen/models/identity/User.js
// module id = 28
// module chunks = 63